import React, { FC, useMemo } from 'react'
import block from 'bem-cn'

import {
  addDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subDays,
} from 'shared/utils/date/getPeriod'

import { DateItem, DateItemState } from '../DateItem'
import { useDatePickerContext } from '../DatePickerProvider/DatePickerContext'
import { DatePickerVariant, IntervalVariants } from '../types'
import { WeekItem } from '../WeekItem'

import './DatesSelection.scss'

const b = block('dates-selection')

type Props = {
  date: Date
}

const DAYS_IN_WEEK = 7

const WEEKS_NUMBERS = Array(DAYS_IN_WEEK)
  .fill(0)
  .map((_, i) => i)

const DatesSelection: FC<Props> = ({ date }) => {
  const { interval, variant } = useDatePickerContext()

  const renderedDates = useMemo(() => {
    const dates =
      interval === IntervalVariants.DAY
        ? eachDayOfInterval({
            start: startOfMonth(date),
            end: endOfMonth(date),
          })
        : eachMonthOfInterval({
            start: startOfYear(date),
            end: endOfYear(date),
          })

    if (interval === IntervalVariants.DAY) {
      const startInterval = dates[0]
      const startIntervalDayWeek = startInterval.getDay() || 7

      if (startIntervalDayWeek !== 1) {
        const prevDates = eachDayOfInterval({
          start: subDays(startInterval, startIntervalDayWeek - 1),
          end: subDays(startInterval, 1),
        })

        dates.unshift(...prevDates)
      }

      const endInterval = dates[dates.length - 1]
      const endIntervalDayWeek = endInterval.getDay()

      if (endIntervalDayWeek !== 0) {
        const nextDates = eachDayOfInterval({
          start: addDays(endInterval, 1),
          end: addDays(endInterval, DAYS_IN_WEEK - endIntervalDayWeek),
        })

        dates.push(...nextDates)
      }
    }

    return dates.map(it => {
      const isOut = interval === IntervalVariants.DAY ? date.getMonth() !== it.getMonth() : false
      const state = isOut ? DateItemState.HIDDEN : undefined

      return <DateItem key={it.toDateString()} date={it} state={state} />
    })
  }, [interval, variant, date])

  const renderedWeeks = useMemo(() => WEEKS_NUMBERS.map(it => <WeekItem key={it} weekNumber={it} />), [])

  return (
    <div className={b('wrapper').mix(variant)}>
      {interval === IntervalVariants.DAY && <div className={b('week-row')}>{renderedWeeks}</div>}
      <div className={b('date-items').mix(interval)}>{renderedDates}</div>
    </div>
  )
}

export { DatesSelection }
